import { tSharedAppState } from 'common/modules/ReactShared';
import {
  AnyAction,
  AsyncThunk,
  createAsyncThunk,
  createSlice,
  Reducer
} from '@reduxjs/toolkit';
import { tPaginationSelectors } from './selectors';
import { IItems, IPagination, IParams, IState, tGetParams, tGetResponse } from './types';

const PER_PAGE_DEFAULT = 24;

type tPaginationSliceConfig<GItem> = {
  name: string;
  getItemsRequest: (params: tGetParams) => Promise<tGetResponse<GItem>>;
  removeItemRequest?: (itemId: number) => Promise<void>;
  selectors: tPaginationSelectors<GItem>;
  perPage?: number;
};

type tPaginationSlice<GItem> = {
  reducer: Reducer<IState<GItem>, AnyAction>;
  actions: {
    getItems: AsyncThunk<
      {
        items: IItems<GItem>;
        params: IParams | null;
        pagination: IPagination;
      },
      IParams | undefined,
      Record<string, never>
    >;
    getMoreItems: AsyncThunk<
      {
        succeeded: boolean;
        nextItems?: IItems<GItem>;
        newPagination?: IPagination;
      },
      undefined,
      { state: tSharedAppState }
    >;
    refreshItems: AsyncThunk<
      {
        items: IItems<GItem>;
        pagination: IPagination;
      },
      undefined,
      { state: tSharedAppState }
    >;
    removeItem: AsyncThunk<void, number, Record<string, never>>;
  };
};

const createPaginationSlice = <GItem>(
  config: tPaginationSliceConfig<GItem>
): tPaginationSlice<GItem> => {
  const perPage = config.perPage ?? PER_PAGE_DEFAULT;
  const { getItemsRequest, removeItemRequest, selectors } = config;

  const initialState: IState<GItem> = {
    items: null,
    params: null,
    pagination: null
  };

  const getItems = createAsyncThunk<
    { items: IItems<GItem>; pagination: IPagination; params: IParams | null },
    IParams | undefined
  >(`${config.name}/getItems_request`, async (params) => {
    const response = await getItemsRequest({ perPage, ...params });
    return { params: params ?? null, ...response };
  });

  const getMoreItems = createAsyncThunk<
    { succeeded: boolean; nextItems?: IItems<GItem>; newPagination?: IPagination },
    undefined,
    { state: tSharedAppState }
  >(`${config.name}/getMoreItems_request`, async (_args, { getState }) => {
    const pagination = selectors.selectPagination(getState());
    const params = selectors.selectParams(getState());
    const nextPage = pagination?.nextPage;
    if (!nextPage) return { succeeded: false };

    const response = await getItemsRequest({
      page: nextPage,
      perPage,
      ...params
    });
    return {
      succeeded: true,
      nextItems: response.items,
      newPagination: response.pagination
    };
  });

  const calculatePaginationFromItemsLength = (
    itemsLength: number,
    totalCount: number
  ): IPagination => {
    const currentPage = Math.ceil(itemsLength / perPage);
    const totalPages = Math.ceil(totalCount / perPage);
    const nextPage = currentPage < totalPages ? currentPage + 1 : null;
    const prevPage = currentPage > 1 ? currentPage - 1 : null;

    return {
      currentPage,
      totalPages,
      nextPage,
      prevPage
    };
  };

  const refreshItems = createAsyncThunk<
    { items: IItems<GItem>; pagination: IPagination },
    undefined,
    { state: tSharedAppState }
  >(`${config.name}/refreshItems_request`, async (_args, { getState }) => {
    const params = selectors.selectParams(getState());
    const currentItems = selectors.selectItems(getState());

    const response = await getItemsRequest({
      perPage: currentItems?.length ?? perPage,
      ...params
    });

    const pagination = calculatePaginationFromItemsLength(
      response.items.length,
      response.pagination.totalCount
    );

    return { items: response.items, pagination };
  });

  const removeItem = createAsyncThunk<void, number>(
    `${config.name}/removeItems_request`,
    async (itemId) => {
      await removeItemRequest?.(itemId);
    }
  );

  const slice = createSlice({
    name: config.name,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(getItems.fulfilled, (_, action) => {
        return action.payload;
      });
      builder.addCase(getMoreItems.fulfilled, (state, action) => {
        const {
          payload: { nextItems, newPagination, succeeded }
        } = action;

        if (!succeeded || !newPagination) return state;

        const oldItems = (state.items as IItems<GItem>) ?? [];
        const items = oldItems.concat(nextItems ?? []);

        return {
          ...state,
          items,
          pagination: newPagination
        };
      });
      builder.addCase(refreshItems.fulfilled, (state, action) => {
        return { ...state, ...action.payload };
      });
    }
  });

  const { reducer } = slice;

  const actions = {
    getItems,
    getMoreItems,
    refreshItems,
    removeItem
  };

  return { reducer, actions };
};

export { createPaginationSlice };
export type { tPaginationSlice, tPaginationSliceConfig };
